/*
 * If not stated otherwise in this file or this component's LICENSE file the
 * following copyright and licenses apply:
 *
 * Copyright 2020 Metrological
 *
 * Licensed under the Apache License, Version 2.0 (the License);
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Lightning, Log, Registry, Router, Utils } from '@lightningjs/sdk';
import { Theme } from './config/theme';
import { Backend } from './api/Backend';
import { MessageDialog } from './pages/dialogs/MessageDialog';
import { PopupTranslationKeys } from './api/models/TranslationKeys';
import { initSyscalls, SysCalls } from './syscalls/Syscalls';
import { MiniDetail } from './components/MiniDetail';
import { debounced } from './Business/utility/Debounced';
import { ProfileApi } from './api/profile/ProfileApi';
import { Login } from './Business/Login';
import { confirmCancelDialog } from './pages/dialogs/ConfirmCancelDialog';
import { convertDataForMiniDetail } from './components/MiniDetailUtils';
import Routes from './Routes';
import { Spinner } from './components/shared/Spinner';
import { Modal } from './widgets/modal/Modal';
import { Dialog } from './widgets/dialog/Dialog';
import { Toasts } from './widgets/toast/Toasts';
import { ScreenSize } from './api/ScreenSize';

export default class App extends Router.App {
  static getFonts() {
    const fonts = [];
    Theme.FontList.forEach(font => {
      fonts.push({ family: font.family, url: Utils.asset(`fonts/${font.name}`) });
    });
    return fonts;
  }

  static _template() {
    return {
      rect: true,
      w: 1920,
      h: 1080,
      color: Theme.Colors.Transparent, // needs to be transparent to display underlying player (defaults to white)
      Pages: {
        w: w => w,
        h: h => h,
        // this hosts all the pages
        forceZIndexContext: true,
      },
      Widgets: {
        w: w => w,
        h: h => h,
        // this hosts all the widgets
        // Widgets are auto zIndexed: first item has lowest, last item has highest
        Modal: {
          type: Modal,
          name: 'Modal',
        },
        PlayerModal: {
          type: Modal,
          name: 'PlayerModal',
        },
        Dialog: {
          type: Dialog,
        },
        Toasts: {
          type: Toasts,
        },
      },
      Loading: {
        x: 0,
        y: 0,
        ...ScreenSize,
        visible: false,
        src: Utils.asset('images/background.png'),
        Spinner: {
          mount: 0.5,
          x: w => w / 2,
          y: h => h / 2,
          type: Spinner,
        },
      },
      Content: {
        type: Lightning.Component,
        x: 0,
        y: 0,
        w: w => w,
        h: h => h,
      },
      MiniDetail: {
        x: 0,
        y: 1080 + 318,
        mountY: 1,
        type: MiniDetail,
        visible: true,
        zIndex: 500,
      },
    };
  }

  _setup() {
    const backend = new Backend(this);
    this._backend = backend;
    initSyscalls();
    SysCalls.onAppStart(this.tag('Content'), this._backend);
    //this.$openAppPage(SplashPage, { backend });
    this.tag('MiniDetail').backend = this._backend;
    this._miniDetailVisible = false;
    this._modalBackStack = [];
    this.focusStack = [];
    Router.startRouter(Routes);
  }

  _getFocused() {
    if (this.focusStack.length !== 0) {
      return this.focusStack[this.focusStack.length - 1];
    } else {
      return Router.getActivePage();
    }
  }

  get currentModal() {
    return this.tag('Modal').childList.last;
  }

  $openAppPage(page, params) {
    if (params != null) {
      Log.info(`[App] Opening page ${page.name} with parameters`, params);
    } else {
      Log.info(`[App] Opening page ${page.name}`);
    }

    this.children.forEach(child => {
      this.clearChildList(child);
      child.visible = false;
    });
    this.tag('MiniDetail').visible = true;

    const content = this.tag('Content');
    content.visible = true;
    if (content) {
      this.focusStack = [];
      this.clearChildList(content);
      content.childList.a({
        w: w => w,
        h: h => h,
        type: page,
        params: params,
      });
      this.focusStack.push(content.childList.last);
      this._refocus();
    }
  }

  $openDialog(template, stack) {
    this.tag('Dialog').$openDialog(template, stack);
  }

  $closeDialog() {
    this.tag('Dialog').$closeDialog();
  }

  $showMessage(message, title = undefined, cta = undefined) {
    this.tag('Dialog').$openDialog({
      type: MessageDialog,
      confirmText: this._backend.translations.forKey(PopupTranslationKeys.popup_btn_ok),
      title,
      message,
      callToAction: cta,
    });
  }

  $forceCloseMessage() {
    this.tag('Dialog').$closeDialog();
  }

  $showToast(text, time = 0) {
    this.tag('Toasts').showToast(text, time);
  }

  $hideToast(text) {
    this.tag('Toasts').hideToast(text);
    this._refocus();
  }

  _handleBack() {
    this.$verifyCloseApp();
  }

  clearChildList(element) {
    if (element === this.tag('MiniDetail')) return;
    element.childList.forEach(child => {
      this.focusStack = this.focusStack.filter(el => el !== child);
    });
    element.childList.clear();
  }

  $verifyCloseApp() {
    const exit = confirmCancelDialog(
      this._backend.translations.forKey(PopupTranslationKeys.popup_exit_title),
      '',
      this.exitApp.bind(this),
      false,
      this._backend.translations.forKey(PopupTranslationKeys.popup_btn_confirm),
      this._backend.translations.forKey(PopupTranslationKeys.popup_btn_cancel)
    );
    this.$openDialog(exit);
  }

  exitApp() {
    // console.log('close App', this._backend);
    if (this._backend.playerController.isStreaming) this._backend.playerController.endStream();
    this._backend.logService.forceSendAnalytics();
    SysCalls.exitApp(this);
  }

  $showMiniDetail() {
    if (!this._miniDetailVisible) {
      this._miniDetailVisible = true;
      this.tag('MiniDetail').setSmooth('y', 1080, { duration: 0.5 });
    }
  }

  $hideMiniDetail() {
    if (this._miniDetailVisible) {
      this._miniDetailVisible = false;
      this.tag('MiniDetail').setSmooth('y', 1080 + 318, { duration: 0.5 });
    }
  }

  $updateMiniDetail(data) {
    if (!this._debouncedUpdate)
      this._debouncedUpdate = debounced(_data => {
        this.tag('MiniDetail').updateUI = convertDataForMiniDetail(_data, this._backend);
      }, 300);

    this._debouncedUpdate(data);
  }

  $switchProfile(profileId) {
    this._backend.playerController.endStream();
    ProfileApi.switch(this._backend, profileId).then(_r => {
      // console.log('Profile switch', r);
      this.$updateSession();
    });
  }

  $updateSession() {
    Login.initSession(this._backend).then(() => {
      const backend = this._backend;
      backend.playerController.fallbackToLinearStream();
      Router.getActivePage().reset();
    });
  }

  _inactive() {
    SysCalls.cleanup(this._backend);
  }

  $setDelayedNavigation(route, time) {
    this._delayedNavTimeout = Registry.setTimeout(() => {
      Router.navigate(route);
      this._delayedNavTimeout = undefined;
    }, time);
  }

  _captureKey() {
    if (this._delayedNavTimeout) {
      Registry.clearTimeout(this._delayedNavTimeout);
      this._delayedNavTimeout = undefined;
    }
    return false;
  }

  // Prevent app being closed by the Router
  _handleAppClose() {
    Log.info('_handleAppClose');
  }
}
